import React, { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { LOGO_HORIZONTAL } from "./Logo";

/**
 * Premium mobile-first image carousel with arrows + swipe + dot indicators
 * and a subtle horizontal-logo watermark on every image.
 */
export default function ImageCarousel({ images, productName }) {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);
  const touchStart = useRef(null);

  const n = images?.length || 0;
  const safeIdx = idx >= n ? 0 : idx;

  if (n === 0) {
    return (
      <div className="flex aspect-[4/5] items-center justify-center rounded-3xl border border-heda-line bg-heda-olive-tint/50">
        <div className="font-display text-6xl font-bold text-heda-olive">{(productName || "H")[0]}</div>
      </div>
    );
  }

  const go = (dir) => {
    setIdx((i) => (i + dir + n) % n);
  };

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStart.current = null;
  };

  return (
    <div className="select-none">
      <div
        className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-heda-line bg-heda-cream shadow-heda"
        ref={trackRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {images.map((img, i) => (
          <img
            key={img.id || i}
            src={fileUrl(img.storage_path)}
            alt={`${productName} ${i + 1}`}
            data-testid={`carousel-image-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === safeIdx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />
        ))}

        {/* Watermark — bottom right, premium and visible */}
        <div className="pointer-events-none absolute bottom-3 right-3 z-10 rounded-xl border border-white/50 bg-white/85 px-3 py-2 shadow-heda backdrop-blur-md md:bottom-4 md:right-4">
          <img src={LOGO_HORIZONTAL} alt="" className="h-7 w-auto opacity-95 mix-blend-multiply md:h-9" />
        </div>

        {/* Arrows */}
        {n > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              data-testid="carousel-prev-button"
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-heda-line bg-white/90 shadow-heda transition-all hover:border-heda-gold hover:bg-white active:scale-95"
            >
              <ChevronLeft size={20} className="text-heda-ink" />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="carousel-next-button"
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-heda-line bg-white/90 shadow-heda transition-all hover:border-heda-gold hover:bg-white active:scale-95"
            >
              <ChevronRight size={20} className="text-heda-ink" />
            </button>
          </>
        )}

        {/* Counter pill */}
        {n > 1 && (
          <div className="absolute left-3 top-3 rounded-full bg-heda-ink/80 px-2.5 py-1 text-[11px] font-medium tabular-nums tracking-wide text-white">
            {safeIdx + 1} / {n}
          </div>
        )}
      </div>

      {/* Dot indicators */}
      {n > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`carousel-dot-${i}`}
              aria-label={`Go to image ${i + 1}`}
              className={`h-1.5 transition-all rounded-full ${
                i === safeIdx ? "bg-heda-gold w-6" : "bg-heda-line w-1.5 hover:bg-heda-gold/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
